import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle2, Loader2, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'artist', label: 'Artist / performer' },
  { value: 'rights_owner', label: 'Rights owner' },
  { value: 'label', label: 'Label' },
  { value: 'manager', label: 'Manager / representative' },
  { value: 'other', label: 'Other' },
];

export default function TakedownPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'artist',
    scope: 'specific',
    work_title: '',
    work_artist: '',
    work_links: '',
    reason: '',
    phone: '',
    signature: '',
    declaration_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error('Please share your name and email');
    if (!form.reason.trim()) return toast.error('Please state your rights and reason');
    if (!form.signature.trim()) return toast.error('Please type your name as signature');
    if (!form.declaration_accepted) return toast.error('Please accept the good-faith declaration');
    if (form.scope === 'specific' && !form.work_title.trim() && !form.work_links.trim()) {
      return toast.error('Add the work title or a link so we can identify it');
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/takedowns`, form);
      setSubmittedRef(res.data);
      toast.success('Takedown request received. A confirmation email is on the way.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedRef) {
    return (
      <div data-testid="takedown-success" className="min-h-screen bg-[#0F1115] text-white pb-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-[#171A21] border border-white/5 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6">
              <CheckCircle2 size={32} className="text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Request received
            </h1>
            <p className="text-[#94A3B8] mb-2">
              Reference:{' '}
              <span className="font-mono text-white/80 text-sm">{submittedRef.id}</span>
            </p>
            <p className="text-[#94A3B8] text-sm mb-8">
              We've emailed you a confirmation. Our team will review your takedown request and get back to you within 5 business days.
            </p>
            <Link
              data-testid="takedown-back-home"
              to="/"
              className="inline-block bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5 text-sm"
            >
              Back to Christovibes
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="takedown-page" className="min-h-screen bg-[#0F1115] text-white pb-32">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Link to="/" data-testid="takedown-back" className="inline-flex items-center gap-1 text-[#94A3B8] hover:text-white text-sm mb-6">
          <ChevronLeft size={16} /> Back
        </Link>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-4">
            <Shield size={14} className="text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-medium tracking-widest uppercase">Rights & Removal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Takedown Request
          </h1>
          <p className="text-[#94A3B8] text-sm sm:text-base max-w-xl">
            If you're an artist or rights owner and would like a specific work — or all of your works — removed from Christovibes, please complete this form. We honour valid DMCA / EU DSA style requests promptly.
          </p>
        </div>

        <form onSubmit={submit} className="bg-[#171A21] border border-white/5 rounded-2xl p-6 sm:p-8 space-y-6">
          {/* Claimant */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Full name *</label>
              <input
                data-testid="td-name"
                type="text"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                required
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Contact email *</label>
              <input
                data-testid="td-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Your role *</label>
              <select
                data-testid="td-role"
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
              >
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Phone (optional)</label>
              <input
                data-testid="td-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
              />
            </div>
          </div>

          {/* Scope */}
          <div>
            <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Scope of request *</label>
            <div className="mt-2 grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="td-scope-specific"
                onClick={() => set('scope', 'specific')}
                className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors ${
                  form.scope === 'specific'
                    ? 'bg-[#D4AF37]/10 border-[#D4AF37]/40 text-white'
                    : 'bg-[#0F1115] border-white/10 text-[#94A3B8] hover:text-white'
                }`}
              >
                <div className="font-medium">A specific work</div>
                <div className="text-xs text-[#64748B] mt-0.5">One track, release or piece of content</div>
              </button>
              <button
                type="button"
                data-testid="td-scope-blanket"
                onClick={() => set('scope', 'blanket')}
                className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors ${
                  form.scope === 'blanket'
                    ? 'bg-[#D4AF37]/10 border-[#D4AF37]/40 text-white'
                    : 'bg-[#0F1115] border-white/10 text-[#94A3B8] hover:text-white'
                }`}
              >
                <div className="font-medium">All of my works</div>
                <div className="text-xs text-[#64748B] mt-0.5">Blanket exclusion from our library</div>
              </button>
            </div>
          </div>

          {/* Work details */}
          {form.scope === 'specific' && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Work title *</label>
                  <input
                    data-testid="td-work-title"
                    type="text"
                    value={form.work_title}
                    onChange={(e) => set('work_title', e.target.value)}
                    className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Artist / release</label>
                  <input
                    data-testid="td-work-artist"
                    type="text"
                    value={form.work_artist}
                    onChange={(e) => set('work_artist', e.target.value)}
                    className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Links / URLs</label>
                <textarea
                  data-testid="td-work-links"
                  rows={2}
                  value={form.work_links}
                  onChange={(e) => set('work_links', e.target.value)}
                  placeholder="e.g. https://christovibes.com/... , Spotify / ISRC / any reference we can use to identify the work"
                  className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 resize-none"
                />
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Statement of rights & reason *</label>
            <textarea
              data-testid="td-reason"
              rows={5}
              value={form.reason}
              onChange={(e) => set('reason', e.target.value)}
              placeholder="Describe the nature of your rights (e.g. sole composer, master rights owner, exclusive licensee) and why you're requesting removal."
              className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 resize-none"
            />
          </div>

          {/* Declaration */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              data-testid="td-declaration"
              type="checkbox"
              checked={form.declaration_accepted}
              onChange={(e) => set('declaration_accepted', e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#D4AF37] cursor-pointer"
              required
            />
            <span className="text-[#94A3B8] text-sm group-hover:text-white transition-colors">
              I have a good-faith belief that the identified use is not authorised by the rights owner, its agent or the law. The information in this notice is accurate, and — under penalty of perjury (or the equivalent under applicable law) — I am the rights owner or authorised to act on their behalf. *
            </span>
          </label>

          {/* Signature */}
          <div>
            <label className="text-xs uppercase tracking-widest text-[#94A3B8] font-medium">Signature (type your full name) *</label>
            <input
              data-testid="td-signature"
              type="text"
              value={form.signature}
              onChange={(e) => set('signature', e.target.value)}
              required
              className="mt-2 w-full bg-[#0F1115] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 font-mono"
            />
          </div>

          <button
            data-testid="td-submit"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : 'Submit takedown request'}
          </button>

          <p className="text-xs text-[#64748B] leading-relaxed">
            Submitting a false or misleading takedown notice may result in liability under applicable law (DMCA §512(f), EU DSA Art. 22, etc.). We may share your notice with the party whose content is affected. See our <Link to="/terms" className="text-[#94A3B8] hover:text-[#D4AF37]">Terms</Link> and <Link to="/privacy" className="text-[#94A3B8] hover:text-[#D4AF37]">Privacy Policy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
